import { Injectable, inject } from '@angular/core';
import { Storage, getBlob, ref } from '@angular/fire/storage';
import { Subject, exhaustMap, filter, firstValueFrom, groupBy, mergeMap, share } from 'rxjs';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { VolumesService } from '@innersight-os/shared/firebase-services';
import { GetThumbnailUrlRequest, GetThumbnailUrlResponse } from '@innersight-os/shared/firebase-functions-interfaces';

@Injectable({
  providedIn: 'root'
})
export class ThumbnailService {
  private thumbnailRequests$ = new Subject<GetThumbnailUrlRequest>()
  private storage: Storage = inject(Storage);
  private volumesService = inject(VolumesService);
  private functions = inject(Functions);
  private thumbnails$ = this.thumbnailRequests$.pipe(
    groupBy(req => JSON.stringify(req)),
    mergeMap(group => group.pipe(
      exhaustMap(this.findThumbnail), // drop requests that are already in flight
    )),
    share({resetOnRefCountZero: false})
  )

  requestThumbnail(req: GetThumbnailUrlRequest): Promise<Response> {
    const responsePromise = firstValueFrom(this.thumbnails$.pipe(
      filter(response => (response.body as unknown as string) === req.volumeId),// without filtering all volumes will show the same thumbnail, the first one returned
    ))
    this.thumbnailRequests$.next(req)
    return responsePromise
  }

  private findThumbnail = async (req: GetThumbnailUrlRequest): Promise<Response> => {
    try {
      const volume = await this.getVolumeFromDb(req.operationId, req.uploadId, req.volumeId)
      let blob: Blob

      if (!volume.thumbnailPath) {
        let avif: string

        try {
          const getThumbnailUrl = httpsCallable<GetThumbnailUrlRequest, GetThumbnailUrlResponse>(this.functions, 'getThumbnailUrl')
          const {data} = await getThumbnailUrl(req)
          const status = data.success.code
          avif = data.avif
          if(status !== 200) return new Response(null, {status})
        } catch(err) {
          console.error('getThumbnailUrl failed: ', err)
          return new Response('getThumbnailUrl failed: ', {status: 500})
        }
    
        try {
          const response = await fetch(avif)
          if(!response.ok) {
            console.error('fetching signedUrl thumbnail bytes failed: ', response)
            return response
          }          
          blob  = await response.blob()
        } catch (err) {
          console.error('fetching signedUrl thumbnail bytes failed: ', err)
          return new Response('fetching signedUrl thumbnail bytes failed: ', {status: 500})
        }

      } else {
        const imageRef = ref(this.storage, volume.thumbnailPath)
        blob = await getBlob(imageRef)
      }

      const response = new Response
      const objectUrl = URL.createObjectURL(blob) 
      Object.defineProperty(response, 'url', {
        value: objectUrl,
        writable: false,
      })
      // previously filtered the url for volumeId but blob doesn't have this path format, hack response.body to store volumeId for later filtering
      // see requestThumbnail()
      Object.defineProperty(response, 'body', {
        value: req.volumeId,
        writable: false,
      })
      return response
    } catch (err) {
      console.error('Failed to fetch thumbnail: ', err)
      return new Response('Failed to fetch thumbnail', { status: 500 })
    }
  }

  private async getVolumeFromDb(operationId: string, uploadId: string, volumeId: string) {
    return firstValueFrom(
      this.volumesService.getVolume(operationId, uploadId, volumeId)
    );
  }
}
